import { useRef } from 'react'
import { Pagination, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { ChevronRightIcon } from '~/components/icons'
import Card from './Card'
import { Button } from '~/components/general/Button'
import { trackEvent } from '~/utils/tracking'
import { Link } from '@remix-run/react'
import { FlexContainer } from '~/components/Grid'
import { TypeResumeExample, TypeResumeExamplesSlider } from '~/types/contentful'

type ResumeExamplesSliderProps = {
  section: TypeResumeExamplesSlider<'WITHOUT_UNRESOLVABLE_LINKS', string>
}

export const ResumeExamplesSlider = ({ section }: ResumeExamplesSliderProps) => {
  const data = section.fields

  const exampleList = (
    data.exampleList as TypeResumeExample<'WITHOUT_UNRESOLVABLE_LINKS', string>[]
  ).map((ex) => ex.fields)

  const prevRef = useRef<HTMLButtonElement | null>(null)
  const nextRef = useRef<HTMLButtonElement | null>(null)

  const handleSlideButtonClick = () => {
    // direction === 'prev' ? swiper.current.prevEl() : swiper.current.nextEl()
    trackEvent('homepage_resume_examples_slider', {
      block: data.blockId as string,
    })
  }

  const handleTemplateItemClick = (resumeExampleName: string, resumeTemplateName: string) => {
    const exampleName = resumeExampleName.includes('Resume Example & Writing Guide')
      ? resumeExampleName.replace('Resume Example & Writing Guide', '')
      : resumeExampleName

    trackEvent('homepage_resume_examples_slider', {
      block: data.blockId as string,
      resume_example_name: exampleName.trim(),
      resume_template_name: resumeTemplateName.trim(),
    })
  }

  return (
    <section
      className="my-25 -mx-8 pt-3 max-xl:p-11 max-lg:my-0 max-lg:-mx-6 max-md:-mx-5 max-md:pt-6 max-md:pb-17 max-md:px-4 "
      data-block-id={data.blockId}
    >
      <FlexContainer justify={'between'} className="items-center my-0 mx-auto gap-8">
        <h2 className="text-m max-md:text-[28px]">{data.title}</h2>
        <FlexContainer
          justify={'end'}
          className="gap-4 items-center !w-[unset] m-0 flex-row-reverse mr-0"
        >
          <Button
            text={data.buttonName}
            type={'link'}
            className="max-xl:!hidden"
            href={data.buttonLink}
            color={'lightGray'}
          />
          <div className="flex [&_.swiper-button-disabled]:bg-none [&_.swiper-button-disabled]:pointer-events-none [&_.swiper-button-disabled]:[&_svg]:[&_path]:stroke-neutral-50">
            <button
              ref={prevRef}
              className="-scale-x-100 text-neutral-80 bg-neutral-5 border-none hover:bg-neutral-10 focus-visible:bg-neutral-10 flex items-center justify-center flex-shrink-0 p-0 m-0 w-12 h-12 rounded-[100%]  cursor-pointer outline-none"
              style={{
                transitionProperty: 'color, background, border-color',
                transitionDuration: '0.175s',
                transitionTimingFunction: 'ease',
              }}
              aria-label="Previous button"
              id="resume-examples-prev-button"
              onClick={handleSlideButtonClick}
            >
              <ChevronRightIcon />
            </button>

            <button
              ref={nextRef}
              className="ml-3 text-neutral-80 bg-neutral-5 border-none hover:bg-neutral-10 focus-visible:bg-neutral-10 flex items-center justify-center flex-shrink-0 p-0 m-0 w-12 h-12 rounded-[100%]  cursor-pointer outline-none"
              style={{
                transitionProperty: 'color, background, border-color',
                transitionDuration: '0.175s',
                transitionTimingFunction: 'ease',
              }}
              aria-label="Next button"
              id="resume-examples-next-button"
              onClick={handleSlideButtonClick}
            >
              <ChevronRightIcon />
            </button>
          </div>
        </FlexContainer>
      </FlexContainer>
      <div className="my-0 mx-auto max-w-[1120px] max-lg:mb-8">
        <div
          className="relative pt-3 overflow-hidden lg:pt-9 xl:pt-5 [&_.swiper]:px-1 [&_.swiper-slide]:flex-col [&_.swiper-slide]:flex-shrink-0 w-full"
          style={{ userSelect: 'none' }}
        >
          <Swiper
            grabCursor
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            onBeforeInit={(swiper) => {
              if (swiper.params.navigation && typeof swiper.params.navigation === 'object') {
                swiper.params.navigation.prevEl = prevRef.current
              }
              if (swiper.params.navigation && typeof swiper.params.navigation === 'object') {
                swiper.params.navigation.nextEl = nextRef.current
              }
            }}
            modules={[Pagination, Navigation]}
            slidesPerView={4}
            spaceBetween={32}
            loop
            breakpoints={{
              1023: {
                slidesPerView: 4,
              },
              800: {
                slidesPerView: 3,
              },
              767: {
                slidesPerView: 2,
              },
              200: {
                slidesPerView: 1.5,
              },
            }}
          >
            {exampleList.map((slide, idx: number) => (
              <SwiperSlide key={idx}>
                <Link
                  className="flex flex-col text-center max-xl:text-left"
                  to={`/resume-examples/${slide.slug}`}
                  onClick={() =>
                    handleTemplateItemClick(slide.title, slide.examplePreviewAlt as string)
                  }
                >
                  <Card resume={slide} />
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
      <a
        className="hidden w-full text-center h-12 my-0 mx-auto justify-center items-center max-md:flex max-md:mt-8 bg-neutral-10"
        style={{
          borderRadius: '100px',
          maxWidth: '1120px',
        }}
        href={data.buttonLink}
      >
        <p className="text-s mb-1">{data.buttonName}</p>
      </a>
    </section>
  )
}
